import {create} from 'zustand';
import {clarity} from 'react-microsoft-clarity';

type streak_config_type = {
  id: number;
  name: string;
  description: string;
  target_count: number;
  user_count: number;
};

type streakDetailsSummaryType = {
  streak_count: number;
  last_streak_date: string | null;
};

interface UserState {
  mobile: string;
  email: string;
  userId: string;
  avatar: string;
  icon: string;
  gems: Number;
  isThirdPartyUser: boolean;
  level: Number;
  progress: Number;
  pointsToNextLevel: Number;
  netScore: Number;
  noOfPuzzlesSolved: number;
  dropDownClose: boolean;
  onboardingState: Number;
  streakConfig: streak_config_type[] | null;
  streakDetailsSummary: streakDetailsSummaryType | null;
  userData: any;
}
// {
//   "id": 1,
//   "name": "Puzzles",
//   "description": "Solve 5 endgame puzzles",
//   "target_count": 5
// }
interface UserActions {
  setMobile: (mobile: string) => void;
  setEmail: (email: string) => void;
  setUserId: (userId: string) => void;
  setGems: (gems: Number) => void;
  setLevel: (level: Number) => void;
  setIsThirdPartyUser: (isThirdPartyUser: boolean) => void;
  setAvatar: (avatar: string) => void;
  setIcon: (icon: string) => void;
  setProgress: (progress: Number) => void;
  setNoOfPuzzlesSolved: (noOfPuzzlesSolved: number) => void;
  setDropDownClose: (value: boolean) => void;
  setOnboardingState: (onboardingState: number) => void;
  setStreakConfig: (streakConfig: streak_config_type[]) => void;
  setStreakDetailsSummary: (
    streakDetailsSummary: streakDetailsSummaryType,
  ) => void;
  setUserData: (userData: any) => void;
}

const useLoggedInUserStore = create<UserState & UserActions>(set => ({
  mobile: '',
  email: '',
  isThirdPartyUser: false,
  userId: '',
  gems: 0,
  level: 0,
  avatar: '',
  icon: '',
  progress: 0,
  netScore: 0,
  streakDetailsSummary: null,
  noOfPuzzlesSolved: 0,
  dropDownClose: false,
  onboardingState: 0,
  pointsToNextLevel: 0,
  streakConfig: null,
  userData: null,
  setUserData: userData => set({userData}),
  setMobile: mobile => set({mobile: mobile}),
  setIsThirdPartyUser: isThirdPartyUser => set({isThirdPartyUser}),
  setEmail: email => set({email: email}),
  setUserId: userId => set({userId: userId}),
  setGems: gems => set({gems: gems}),
  setLevel: level => set({level: level}),
  setAvatar: avatar => set({avatar: avatar}),
  setIcon: icon => set({icon: icon}),
  setProgress: progress => set({progress: progress}),
  setNoOfPuzzlesSolved: noOfPuzzlesSolved => set({noOfPuzzlesSolved}),
  setDropDownClose: value => set({dropDownClose: value}),
  setOnboardingState: onboardingState => set({onboardingState}),
  setStreakConfig: streakConfig => set({streakConfig}),
  setStreakDetailsSummary: streakDetailsSummary => set({streakDetailsSummary}),
}));

/**
 * Keep the denormalized fields used throughout the app in sync with the
 * dashboard summary returned after login.
 */
export const hydrateLoggedInUserStore = (summary: any) => {
  const user = summary?.user ?? null;
  const avatar = summary?.avatar ?? null;

  useLoggedInUserStore.setState({
    userData: user,
    userId:
      user?.user_id == null
        ? user?.username?.toString?.() ?? ''
        : user.user_id.toString(),
    email: user?.email ?? '',
    mobile: user?.mobile_number?.toString?.() ?? '',
    avatar: avatar?.avatar ?? '',
    icon: avatar?.avatarDP ?? '',
    gems: avatar?.gems ?? 0,
    level: avatar?.level == null ? 0 : avatar.level + 1,
    progress: avatar?.progress ?? 0,
    pointsToNextLevel:
      avatar?.points_to_next_level == null
        ? 0
        : avatar.points_to_next_level + (avatar?.gems ?? 0),
    netScore: user?.net_score ?? 0,
    streakConfig: summary?.streak_details ?? null,
    streakDetailsSummary: summary?.streak_details_summary ?? null,
  });

  // Tag Clarity sessions with the real user id (not PII like mobile/email).
  const userId = useLoggedInUserStore.getState().userId;
  if (userId && clarity.hasStarted()) {
    clarity.identify(userId);
  }
};

export default useLoggedInUserStore;
